import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que el componente de adjuntos le pide a la API (B-26). Misma forma que el
// `datos.ts` de sucursales: clave de caché con el taller, una consulta por lectura
// y una función por escritura que devuelve el `ProblemDetails` o nada.
//
// **Las fotos cuelgan de su dueño** (#142): las del activo van por
// `/api/attachments`; las del comprobante, por las rutas del gasto; y las de la
// orden y sus líneas, por las de la orden (#140). Cada una con su permiso.
// `Origen` dice de cuál se trata y cada función elige la ruta.

export type Adjunto = components['schemas']['AttachmentListItem'];

/**
 * De quién cuelgan los adjuntos: un activo, el comprobante de un gasto o una
 * orden. La orden abarca las fotos de sus líneas: su galería las trae todas, y
 * cada una dice de cuál es (`detailId`).
 */
export type Origen = { tipo: 'activo'; id: string } | { tipo: 'gasto'; id: string } | { tipo: 'orden'; id: string };

/** Los tipos que acepta la API, para el selector de archivos. Si difieren, manda la API. */
export const TIPOS_ACEPTADOS = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

/** El taller de la pestaña (#205), que va en toda clave de caché: regla 5 de multitenancy. */
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  delOrigen: (taller: string, origen: Origen) => ['adjuntos', taller, origen.tipo, origen.id] as const,
  url: (taller: string, adjunto: string) => ['adjuntos', taller, 'url', adjunto] as const,
};

/** Una página de la lista, por la ruta de su dueño. */
function pedirPagina(origen: Origen, cursor: string | undefined) {
  const pagina = { limit: 100, ...(cursor && { cursor }) };
  switch (origen.tipo) {
    case 'activo':
      return api.GET('/api/attachments', { params: { query: { assetId: origen.id, ...pagina } } });
    case 'gasto':
      return api.GET('/api/expenses/{id}/attachments', { params: { path: { id: origen.id }, query: pagina } });
    case 'orden':
      return api.GET('/api/service-orders/{id}/attachments', { params: { path: { id: origen.id }, query: pagina } });
  }
}

/**
 * Los adjuntos de un activo, de un gasto o de una orden. Se recorren todas las
 * páginas: un dueño tiene decenas de fotos, no miles, y la galería las muestra
 * juntas.
 */
export function useAdjuntos(origen: Origen) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.delOrigen(taller, origen),
    queryFn: async () => {
      const todos: Adjunto[] = [];
      let cursor: string | undefined;
      do {
        const { data, error } = await pedirPagina(origen, cursor);
        if (error || !data) throw new Error('No se pudo leer la lista de adjuntos');
        todos.push(...data.items);
        cursor = data.hasMore && data.nextCursor ? data.nextCursor : undefined;
      } while (cursor);
      return todos;
    },
  });
}

function pedirUrl(origen: Origen, adjunto: string) {
  switch (origen.tipo) {
    case 'activo':
      return api.GET('/api/attachments/{id}/url', { params: { path: { id: adjunto } } });
    case 'gasto':
      return api.GET('/api/expenses/{id}/attachments/{attachmentId}/url', {
        params: { path: { id: origen.id, attachmentId: adjunto } },
      });
    case 'orden':
      return api.GET('/api/service-orders/{id}/attachments/{attachmentId}/url', {
        params: { path: { id: origen.id, attachmentId: adjunto } },
      });
  }
}

/**
 * La URL firmada de un adjunto, para la miniatura o la descarga.
 *
 * **Se vuelve a pedir antes de que caduque**: la API la firma por minutos, y una
 * galería abierta más que eso mostraría imágenes rotas. Un minuto antes del
 * vencimiento la da por vieja y pide otra.
 */
export function useUrlAdjunto(origen: Origen, adjunto: string) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.url(taller, adjunto),
    queryFn: async () => {
      const { data, error } = await pedirUrl(origen, adjunto);
      if (error || !data) throw new Error('No se pudo firmar el adjunto');
      return data;
    },
    staleTime: (consulta) => {
      const vence = consulta.state.data ? Date.parse(consulta.state.data.expiresAt) : 0;
      return Math.max(0, vence - Date.now() - 60_000);
    },
    refetchInterval: (consulta) => {
      const vence = consulta.state.data ? Date.parse(consulta.state.data.expiresAt) : 0;
      return vence ? Math.max(5_000, vence - Date.now() - 60_000) : false;
    },
  });
}

/**
 * Sube un archivo y devuelve el `ProblemDetails` si falló. Suelta, sin caché,
 * porque la usa también el alta del gasto (#143): el comprobante se sube después
 * de registrarlo, cuando el gasto ya tiene id y la galería todavía no existe.
 *
 * `linea`, solo con una orden: la foto es de esa línea y no de la orden en general
 * (#140). La galería de la orden la trae igual.
 *
 * **Sin id**: lo pone la API, que lo genera ordenable por tiempo, y la galería se
 * ordena por él. El id del cliente existe para la PWA, que reintenta subidas
 * encoladas; acá no hay cola que reintentar.
 *
 * El contrato tipa el archivo como `string` porque así describe OpenAPI a un
 * `IFormFile`; lo que viaja es el `File`, armado a mano en el `FormData`.
 */
export async function subirAdjunto(origen: Origen, archivo: File, linea?: string): Promise<ProblemaApi | undefined> {
  const formulario = new FormData();
  if (origen.tipo === 'activo') formulario.append('assetId', origen.id);
  if (origen.tipo === 'orden' && linea) formulario.append('detailId', linea);
  formulario.append('file', archivo, archivo.name);
  const enviar = () => formulario;
  const file = archivo as unknown as string;

  const { error } =
    origen.tipo === 'activo'
      ? await api.POST('/api/attachments', { body: { assetId: origen.id, file }, bodySerializer: enviar })
      : origen.tipo === 'gasto'
        ? await api.POST('/api/expenses/{expenseId}/attachments', {
            params: { path: { expenseId: origen.id } },
            body: { file },
            bodySerializer: enviar,
          })
        : await api.POST('/api/service-orders/{serviceOrderId}/attachments', {
            params: { path: { serviceOrderId: origen.id } },
            body: { file, ...(linea && { detailId: linea }) },
            bodySerializer: enviar,
          });
  return error ? (error as ProblemaApi) : undefined;
}

function quitarAdjunto(origen: Origen, id: string) {
  switch (origen.tipo) {
    case 'activo':
      return api.DELETE('/api/attachments/{id}', { params: { path: { id } } });
    case 'gasto':
      return api.DELETE('/api/expenses/{id}/attachments/{attachmentId}', {
        params: { path: { id: origen.id, attachmentId: id } },
      });
    case 'orden':
      return api.DELETE('/api/service-orders/{id}/attachments/{attachmentId}', {
        params: { path: { id: origen.id, attachmentId: id } },
      });
  }
}

export function useCambiosAdjuntos(origen: Origen) {
  const taller = useTaller();
  const cache = useQueryClient();

  async function tras(peticion: Promise<ProblemaApi | undefined>): Promise<ProblemaApi | undefined> {
    const error = await peticion;
    if (error) return error;
    await cache.invalidateQueries({ queryKey: claves.delOrigen(taller, origen) });
    return undefined;
  }

  return {
    /**
     * Vuelve a leer la galería, para lo que da de baja fotos por otro camino:
     * quitar una línea se lleva las suyas en el servidor (#129, #140).
     */
    refrescar: () => cache.invalidateQueries({ queryKey: claves.delOrigen(taller, origen) }),
    /** `linea`, solo con una orden: ver `subirAdjunto`. */
    subir: (archivo: File, linea?: string) => tras(subirAdjunto(origen, archivo, linea)),
    eliminar: async (id: string) => {
      const { error } = await quitarAdjunto(origen, id);
      return tras(Promise.resolve(error ? (error as ProblemaApi) : undefined));
    },
  };
}

/** Si el adjunto se puede mostrar como imagen. HEIC no: casi ningún navegador lo dibuja. */
export const esImagen = (a: Adjunto) => /^image\/(jpeg|png|webp)$/i.test(a.contentType);
